import { auth, firebaseEnabled } from '../firebase'
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword
} from 'firebase/auth'

const LS_SESSION_KEY = 'etimad_admin_session'

// Placeholder credentials used only while Firebase Auth isn't configured yet.
// Once VUE_APP_FIREBASE_* env vars are set, real users created in the
// Firebase console (Authentication > Users) are used instead and this
// fallback is bypassed entirely.
const DEMO_EMAIL = 'admin@etimad.com'
const DEMO_PASSWORD = 'admin123'

export function isUsingDemoAuth() {
  return !firebaseEnabled
}

export function getDemoCredentials() {
  return { email: DEMO_EMAIL, password: DEMO_PASSWORD }
}

export async function login(email, password) {
  if (firebaseEnabled) {
    const cred = await signInWithEmailAndPassword(auth, email, password)
    return { email: cred.user.email }
  }

  if (email === DEMO_EMAIL && password === DEMO_PASSWORD) {
    localStorage.setItem(LS_SESSION_KEY, JSON.stringify({ email }))
    return { email }
  }

  throw new Error('Invalid email or password.')
}

export async function changePassword(oldPassword, newPassword) {
  if (!firebaseEnabled) {
    throw new Error('Changing the password requires Firebase to be connected. Set up your Firebase project first.')
  }

  const user = auth.currentUser
  if (!user || !user.email) {
    throw new Error('You must be signed in to change your password.')
  }

  try {
    const credential = EmailAuthProvider.credential(user.email, oldPassword)
    await reauthenticateWithCredential(user, credential)
  } catch (e) {
    throw new Error('Old password is incorrect.')
  }

  await updatePassword(user, newPassword)
}

export async function logout() {
  if (firebaseEnabled) {
    await signOut(auth)
    return
  }
  localStorage.removeItem(LS_SESSION_KEY)
}

// Calls back with the current user ({ email }) or null, immediately and on
// every subsequent auth change. Returns an unsubscribe function.
export function watchAuthState(callback) {
  if (firebaseEnabled) {
    return onAuthStateChanged(auth, user => {
      callback(user ? { email: user.email } : null)
    })
  }

  const readSession = () => {
    const raw = localStorage.getItem(LS_SESSION_KEY)
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch (e) {
      return null
    }
  }

  callback(readSession())

  const handler = event => {
    if (event.key === LS_SESSION_KEY) {
      callback(readSession())
    }
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

// One-time check used by the router guard: resolves with the current user
// ({ email }) or null as soon as the auth state is known.
export function getCurrentUser() {
  return new Promise(resolve => {
    const unsubscribe = watchAuthState(user => {
      resolve(user)
      Promise.resolve().then(unsubscribe)
    })
  })
}
